import { AxiosInstance } from "axios";
import { Duration } from "date-fns";
import { AttachmentCategory } from "components/Modals/OccurrenceManagementModal/Tabs/AttachmentsTab/data";
import { VehicleTabData } from "components/Modals/OccurrenceManagementModal/Tabs/VehicleDataTab/reducers";
import { client } from "./api";
import { ReportValues } from "interfaces/Report";
import { ValueTypes } from "components/Modals/OccurrenceManagementModal/Tabs/PaymentTab/data";

type FinishOccurrenceParams = {
  occurrenceId: string;
  policemanId?: string;
  superficialPolicemanId?: string;
};

type AcceptOccurrenceParams = {
  occurrenceId: string;
  policemenId?: string[];
  superficialPolicemen?: {
    name: string;
    phoneNumber: string;
  }[];
  type: number;
};

export interface ISendManualOccurrencePicture {
  reportId: string;
  category?: AttachmentCategory.Patrimony | AttachmentCategory.Vehicle | 'no_category';
  picture: {
    caption: string;
    location: string[];
    cameraData: File[];
  };
}

export interface IManualOccurrencePayment {
  paymentValues: ValueTypes;
  productRegionId: string;
}

class OccurrenceManagementService {
  client: AxiosInstance = client();

  async finishOccurrence({
    occurrenceId,
    policemanId,
    superficialPolicemanId,
  }: FinishOccurrenceParams): Promise<void> {
  
    const api = client();
    const httpResponse = await api.post("occurrences/manual/finish", {
      occurrenceId,
      policemanId,
      superficialPolicemanId,
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async acceptOccurrence({
    occurrenceId,
    type,
    policemenId,
    superficialPolicemen,
  }: AcceptOccurrenceParams): Promise<void> {
    const api = client();
    const httpResponse = await api.post("occurrences/manual/accept", {
      occurrenceId,
      type,
      policemenId,
      superficialPolicemen,
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateArrival(id: string, arrivalTime: Date) {
    const api = client();
    const httpResponse = await api.patch("reports", {
      id,
      arrivalTime,
      status: 'andamento'
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateSuperficialClient(id: string, superficialClientId: string) {
    const api = client();
    const httpResponse = await api.patch("occurrences", {
      occurrence: {
        id,
        superficialClient: { id: superficialClientId },
      },
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateBONumber(id: string, numberBO: number) {
    const api = client();
    const httpResponse = await api.patch("occurrences", {
      occurrence: {
        id,
        numberBO,
      },
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateOccurrenceValues(id: string, valueObject: ReportValues) {
    let valueObjectWithNoEmptyValues: Partial<ReportValues> = {};

    Object.entries(valueObject).forEach(([key, value]) => {
      if (!Boolean(value)) {
        return;
      }

      valueObjectWithNoEmptyValues[key as keyof ReportValues] = Number(value);
    });

    const api = client();
    const httpResponse = await api.patch("reports", {
      id,
      ...valueObjectWithNoEmptyValues,
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateClient(id: string, clientId: string) {
    const api = client();
    const httpResponse = await api.patch("occurrences", {
      occurrence: {
        id,
        client: clientId,
      },
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateVehicleData(id: string, vehicleData: Partial<VehicleTabData>) {
    const api = client();
    const httpResponse = await api.patch("occurrences", {
      superficialVehicle: {
        ...vehicleData,
      },
      occurrence: {
        id,
      },
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateLastCoords(id: string, lastCoords: [number, number][], formattedAddress: string) {
    const api = client();
    const httpResponse = await api.patch("occurrences", {
      occurrence: {
        id,
        stringLastCoords: JSON.stringify(lastCoords),
        lastCoordAddress: formattedAddress,
      },
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async removeLastCoord(id: string, lastCoords: [number, number][]) {
    const api = client();
    const httpResponse = await api.patch("occurrences", {
      occurrence: {
        id,
        stringLastCoords: JSON.stringify(lastCoords),
      },
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateFoundCoords(id: string, findCoords: [number, number]) {
    const api = client();
    const httpResponse = await api.patch("occurrences", {
      occurrence: {
        id,
        stringFindCoords: JSON.stringify(findCoords),
      },
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateEstimatedArrival(id: string, estimatedDurationArrival: Duration) {
    const api = client();
    const httpResponse = await api.patch("reports", {
      id,
      estimatedDurationArrival,
      status: 'em-percurso'
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateLeaveTime(id: string, leaveTime: Date) {
    const api = client();
    const httpResponse = await api.patch("reports", {
      id,
      leaveTime
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async updateReportPayment(reportId: string, data: IManualOccurrencePayment) {
    const api = client();
    const httpResponse = await api.patch("reports/payment", {
      reportId,
      recValues: {
        ...data.paymentValues.client,
        productRegionId: data.productRegionId,
      },
      prValues: data.paymentValues.policeman,
    });

    if (httpResponse.status === 200) return;
    throw new Error();
  }

  async send({ category, reportId, picture }: ISendManualOccurrencePicture) {
    const attachment = new FormData();
    attachment.append("reportId", reportId);
    attachment.append("category", category ?? 'none');
    attachment.append("caption", picture.caption);

    for (const pictureLocation of picture.location) {
      attachment.append("location", pictureLocation)
    }

    for (const pictureItem of picture.cameraData) {
      attachment.append("pictures", pictureItem);
    }

    const attachmentClient = client({
      "Content-Type":
        "multipart/form-data; boundary=PRMA-POLICEMAN-ATTACHMENT",
    })

    const httpResponse = await attachmentClient.post(
      "report/attachments",
      attachment,
    );

    if (httpResponse.status === 200) return;
    throw new Error();
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new OccurrenceManagementService();
